import React, { useState, useEffect, useMemo } from "react";
import { Dialog } from "../containers/Dialog.tsx";
import { Button } from "../widgets/Button.tsx";
import { TextInput } from "../inputs/TextInput.tsx";
import {
  useCreateWorkout,
  usePatchWorkout,
  useCreateExercise,
} from "../../api/hooks.ts";
import {
  CreateWorkoutRequest,
  PatchWorkoutRequest,
  ExerciseSet,
  Exercise,
  PatchExerciseReference,
  ExerciseDetails,
  WorkoutDetails,
} from "../../api/types.ts";
import { isNotNullish } from "../../util/typeGuards.ts";
import { useDialog } from "../DialogManager.tsx";
import { DatePicker } from "../inputs/DatePicker.tsx";
import { TimePicker } from "../inputs/TimePicker.tsx";
import { WorkoutExerciseList } from "../workouts/WorkoutExerciseList.tsx";
import { Select } from "../inputs/Select.tsx";
import { RadioButtons } from "../inputs/RadioButtons.tsx";
import { FetchedExercise } from "../../util/exercise.tsx";

interface WorkoutValidationErrors {
  date?: string;
  name?: string;
}

enum WorkoutDialogStep {
  Basic = 1,
  Exercises = 2,
}

interface WorkoutDialogProps {
  open: boolean;
  closeDialog: () => void;
  workout?: WorkoutDetails;
  preferredResistanceUnit: "kg" | "lbs";
  preferredVisibility: "private" | "friends";
  preferredTimeFormat: "12h" | "24h";
  exercises: Exercise[];
}

const WorkoutDialog: React.FC<WorkoutDialogProps> = ({
  open,
  closeDialog,
  workout,
  preferredResistanceUnit,
  preferredVisibility,
  preferredTimeFormat,
  exercises,
}) => {
  const [step, setStep] = useState<WorkoutDialogStep>(WorkoutDialogStep.Basic);

  const [workoutName, setWorkoutName] = useState<string | undefined>(undefined);
  const [workoutDate, setWorkoutDate] = useState<Date>(new Date());
  const [status, setStatus] = useState("active");
  const [visibility, setVisibility] = useState(preferredVisibility);
  const [validationErrors, setValidationErrors] =
    useState<WorkoutValidationErrors>({});
  const [workoutExercises, setWorkoutExercises] = useState<
    PatchExerciseReference[]
  >([]);

  const availableExercises = useMemo(() => {
    const available = new Map<string, FetchedExercise>();

    exercises.forEach((ex) =>
      available.set(ex.id, { exercise: ex, external: false }),
    );

    return available;
  }, [exercises]);

  const availableExerciseSnapshots = useMemo(() => {
    if (!workout) {
      return undefined;
    }

    const available = new Map<string, ExerciseDetails>();

    workout.exercises
      .filter((ex) => !!ex.snapshot_id)
      .forEach((ex) => available.set(ex.snapshot_id!, ex));

    return available;
  }, [workout]);

  const defaultWorkoutExercises: ExerciseDetails[] = workoutExercises.map(
    (e) => {
      console.log(e);
      console.log(workout?.exercises);
      const snapshot = workout?.exercises.find(
        (existing) => existing.snapshot_id === e.snapshot_id,
      );
      const exercise = exercises.find(
        (existing) => existing.id === e.exercise_id,
      ) ?? {
        primary_muscle_groups: snapshot?.primary_muscle_groups ?? [],
        secondary_muscle_groups: snapshot?.secondary_muscle_groups ?? [],
        name: snapshot ? `${snapshot?.name} (snapshot)` : "Unknown Exercise",
        start_resistance: snapshot?.start_resistance ?? 0,
        efficiency_factor: snapshot?.efficiency_factor ?? 1.0,
      };
      return { ...e, ...exercise };
    },
  );

  const createWorkoutMutation = useCreateWorkout();
  const patchWorkoutMutation = usePatchWorkout();
  const createExerciseMutation = useCreateExercise();

  const { setError, openDialog } = useDialog();

  // Update workoutName when workout prop changes
  useEffect(() => {
    if (isNotNullish(workout)) {
      setWorkoutName(workout.name);
      setWorkoutDate(new Date(workout.executed_at));
      setWorkoutExercises(workout.exercises);
      setStatus(workout.status);
      setVisibility(workout.visibility);
    } else {
      setWorkoutName(undefined);
      setWorkoutDate(new Date());
      setWorkoutExercises([]);
      setStatus("active");
      setVisibility(preferredVisibility);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workout]);

  useEffect(() => {
    if (
      createExerciseMutation.isError ||
      createWorkoutMutation.isError ||
      patchWorkoutMutation.isError
    ) {
      const apiError =
        createExerciseMutation.error ??
        createWorkoutMutation.error ??
        patchWorkoutMutation.error ??
        new Error("Unknown error occurred");

      setError(apiError);
    }
  }, [
    createExerciseMutation.isError,
    createWorkoutMutation.isError,
    patchWorkoutMutation.isError,
    createExerciseMutation.error,
    patchWorkoutMutation.error,
    createWorkoutMutation.error,
    setError,
  ]);

  useEffect(() => {
    setValidationErrors((current) => ({ ...current, name: undefined }));
  }, [workoutName]);

  useEffect(() => {
    setValidationErrors((current) => ({ ...current, date: undefined }));
  }, [workoutDate]);

  const placeHolderWorkoutName = () => {
    if (workoutDate.getHours() < 12) {
      return "Morning Workout";
    } else if (workoutDate.getHours() < 18) {
      return "Afternoon Workout";
    } else {
      return "Evening Workout";
    }
  };

  const validateWorkoutInputs = ({
    name,
    date,
  }: {
    name: string;
    date: Date;
  }) => {
    const errors: WorkoutValidationErrors = {};

    if (name.length > 30) {
      errors.name = "Workout name must be 30 characters or less";
    }

    if (date > new Date()) {
      errors.date = "Workout date cannot be in the future";
    }

    return errors;
  };

  function moveWorkoutExercise(
    workoutExercises: PatchExerciseReference[],
    oldIndex: number,
    newIndex: number,
  ) {
    workoutExercises.splice(
      newIndex,
      0,
      workoutExercises.splice(oldIndex, 1)[0],
    );
  }

  const handleWorkoutExerciseChange = (
    workoutExerciseIndex: number,
    newUnit: "kg" | "lbs",
    newExerciseId?: string,
    newSnapshotId?: string,
    newWorkoutExerciseIndex?: number,
  ) => {
    console.log(newSnapshotId);
    const updatedWorkoutExercises = workoutExercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        return {
          ...ex,
          exercise_id: newExerciseId,
          snapshot_id: newSnapshotId,
          resistance_unit: newUnit,
        };
      }
      return ex;
    });
    if (isNotNullish(newWorkoutExerciseIndex)) {
      moveWorkoutExercise(
        updatedWorkoutExercises,
        workoutExerciseIndex,
        newWorkoutExerciseIndex,
      );
    }
    setWorkoutExercises(updatedWorkoutExercises);
  };

  const handleDeleteWorkoutExercise = (workoutExerciseIndex: number) => {
    const updatedWorkoutExercises = workoutExercises.filter(
      (_, idx) => idx !== workoutExerciseIndex,
    );
    setWorkoutExercises(updatedWorkoutExercises);
  };

  const handleAddWorkoutExercise = (exerciseId: string) => {
    const fetched = availableExercises.get(exerciseId);

    if (!fetched?.exercise) {
      return;
    }
    const updatedWorkoutExercises = [
      ...workoutExercises,
      {
        exercise_id: exerciseId,
        sets: [] as ExerciseSet[],
        resistance_unit:
          fetched.exercise.default_resistance_unit ?? preferredResistanceUnit,
      },
    ];
    setWorkoutExercises(updatedWorkoutExercises);
  };

  const handleAddSet = (
    workoutExerciseIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedWorkoutExercises = workoutExercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        return {
          ...ex,
          sets: [...ex.sets, { repetitions, resistance }],
        };
      }
      return ex;
    });
    setWorkoutExercises(updatedWorkoutExercises);
  };

  const handleEditSet = (
    workoutExerciseIndex: number,
    setIndex: number,
    repetitions: number,
    resistance: number,
  ) => {
    const updatedWorkoutExercises = workoutExercises.map((ex, idx) => {
      if (idx === workoutExerciseIndex) {
        // Delete set if both repetitions and resistance are zero
        if (repetitions === 0 && resistance === 0) {
          const updatedSets = ex.sets.filter((_, index) => index !== setIndex);
          return {
            ...ex,
            sets: updatedSets,
          };
        }

        // Otherwise, update the set
        const updatedSets = ex.sets.map((set, index) => {
          if (index === setIndex) {
            return { repetitions, resistance };
          }
          return set;
        });
        return {
          ...ex,
          sets: updatedSets,
        };
      }
      return ex;
    });
    setWorkoutExercises(updatedWorkoutExercises);
  };

  const handleSaveWorkout = (workout?: WorkoutDetails) => {
    const errors = validateWorkoutInputs({
      name: workoutName ?? placeHolderWorkoutName(),
      date: workoutDate,
    });
    setValidationErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    if (workout) {
      const workoutData: PatchWorkoutRequest = {
        id: workout.id,
        name: workoutName ?? placeHolderWorkoutName(),
        executed_at: workoutDate.toISOString(),
        exercises: workoutExercises,
      };

      patchWorkoutMutation.mutate(
        { id: workout.id, workout: workoutData },
        {
          onSuccess: () => {
            closeDialog();
            setStep(WorkoutDialogStep.Basic);
          },
          onError: (error) => {
            setError(error as Error);
          },
        },
      );
    } else {
      const workoutData: CreateWorkoutRequest = {
        name: workoutName ?? placeHolderWorkoutName(),
        exercises: workoutExercises,
        executed_at: workoutDate.toISOString(),
        status: status as "active" | "completed",
        visibility: visibility as "private" | "friends",
      };

      createWorkoutMutation.mutate(workoutData, {
        onSuccess: () => {
          closeDialog();
          setStep(WorkoutDialogStep.Basic);
        },
        onError: (error) => {
          setError(error as Error);
        },
      });
    }
  };

  const handleClose = () => {
    if (
      !createWorkoutMutation.isPending &&
      !(workout && patchWorkoutMutation.isPending)
    ) {
      createWorkoutMutation.reset();
      patchWorkoutMutation.reset();
      closeDialog();
      setStep(WorkoutDialogStep.Basic);
      setValidationErrors({});
    }
  };

  const handleProceedOrSave = () => {
    const validationErrors = validateWorkoutInputs({
      name: workoutName ?? placeHolderWorkoutName(),
      date: workoutDate,
    });
    setValidationErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    if (step === WorkoutDialogStep.Basic && !workout) {
      setStep(WorkoutDialogStep.Exercises);
    } else {
      handleSaveWorkout(workout);
    }
  };

  const handleBackOrCancel = () => {
    if (step === WorkoutDialogStep.Basic) {
      handleClose();
    } else {
      setStep(WorkoutDialogStep.Basic);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={isNotNullish(workout) ? "Edit Workout" : "Record Workout"}
    >
      <Dialog.Content>
        {step === WorkoutDialogStep.Basic ? (
          <div className="flex flex-col gap-2">
            {!workout && <p>Give your workout a name and add details.</p>}
            <TextInput
              label="Name"
              value={workoutName}
              onChange={(e) => setWorkoutName(e.target.value)}
              placeholder={placeHolderWorkoutName()}
              errorText={validationErrors.name}
              disabled={
                createWorkoutMutation.isPending ||
                patchWorkoutMutation.isPending
              }
            />
            <div className="flex flex-wrap gap-x-4">
              <DatePicker
                label="Date"
                value={workoutDate}
                onChange={setWorkoutDate}
                errorText={validationErrors.date}
              />
              <TimePicker
                label="Time"
                value={workoutDate}
                onChange={setWorkoutDate}
                format={preferredTimeFormat}
              />
            </div>
            <p>
              <label className="block text-sm font-medium text-gray-700">
                Exercises
              </label>
              {!workout && (
                <span className="text-sm">
                  The workout exercises can be edited after saving.{" "}
                </span>
              )}
              <span className="text-sm">
                <button
                  onClick={() => openDialog("create-exercise")}
                  className="text-secondary underline"
                >
                  Click here
                </button>{" "}
                to add a new exercise to your library.
              </span>
            </p>
            {availableExercises.size > 0 && (
              <WorkoutExerciseList
                resourceId={workout?.id ?? ""}
                workoutExercises={defaultWorkoutExercises}
                availableExercises={availableExercises}
                availableExerciseSnapshots={availableExerciseSnapshots}
                onExerciseChange={handleWorkoutExerciseChange}
                onDeleteExercise={handleDeleteWorkoutExercise}
                onAddExercise={handleAddWorkoutExercise}
                onAddSet={handleAddSet}
                onEditSet={handleEditSet}
              />
            )}
          </div>
        ) : (
          step === WorkoutDialogStep.Exercises && (
            <div className="flex flex-col gap-2">
              {workout?.status !== "completed" && (
                <>
                  <Select
                    label="Visibility"
                    value={visibility}
                    options={[
                      { id: "private", label: "Keep it private" },
                      {
                        id: "friends",
                        label: "Upload to activity feed",
                      },
                    ]}
                    onChange={(value) =>
                      setVisibility(value as "private" | "friends")
                    }
                  />
                  {visibility === "friends" ? (
                    <p className="text-sm">
                      This workout will be visible to your friends when it's
                      completed.
                    </p>
                  ) : (
                    <p className="text-sm">
                      This workout will <b>not</b> be visible to your friends
                      when it's completed.
                    </p>
                  )}
                </>
              )}
              <RadioButtons
                label="Status"
                value={status}
                options={[
                  {
                    id: "active",
                    label: "In Progress",
                  },
                  { id: "completed", label: "Completed" },
                ]}
                onChange={setStatus}
              />
            </div>
          )
        )}
      </Dialog.Content>
      <Dialog.Actions>
        {step !== WorkoutDialogStep.Basic && !workout && (
          <Button
            variant="neutral"
            onClick={handleBackOrCancel}
            disabled={
              createWorkoutMutation.isPending || patchWorkoutMutation.isPending
            }
          >
            Back
          </Button>
        )}
        <Button
          variant="submit"
          onClick={handleProceedOrSave}
          loading={
            createExerciseMutation.isPending || patchWorkoutMutation.isPending
          }
          disabled={
            validationErrors.name !== undefined ||
            validationErrors.date !== undefined ||
            (step === WorkoutDialogStep.Exercises &&
              availableExercises.size === 0)
          }
        >
          {step === WorkoutDialogStep.Basic && !workout ? "Next" : "Save"}
        </Button>
      </Dialog.Actions>
    </Dialog>
  );
};

export { WorkoutDialog };
